import type {UserImageVOResponse} from '@Libraries/user-image-library';
import type {MediaItemSizeType} from '@Libraries/s3-library';
import {getDirForMediaSizeType, repoURL} from '@Libraries/s3-library';
import {getWriteBucket} from '@Utils/s3.util';
import type {AITextToImageBackendData} from '@Libraries/pmw-stock-media-library';
import {deleteGeneratedImage} from '@Libraries/pmw-stock-media-library';
import {deleteGridItems} from '@Components/base-grid';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ColorTypes} from '@Components/controlled-list/components/controlled-list-item-type-7/controlled-list-item-type-7';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';
import {isMobile} from 'react-device-detect';
import {openAiEditModal} from '@Modals/poster-editor-ai-edit-modal/poster-editor-ai-edit-modal';
import {updateFeatureToShowOnLoad} from '@/components/poster-editor/poster-editor-reducer';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';

const BASE_DIR = 'aiimages';

export interface TextToImageUploadProps {
  userId?: number;
  aiGeneratedImageId: number;
}

export const textToImageUpload = async (p: TextToImageUploadProps): Promise<UserImageVOResponse> => {
  return (await window.PMW.writeLocal('stockmedia/uploadGeneratedImage', p)) as UserImageVOResponse;
};

export const showTextToImageUpsellDialog = async (): Promise<void> => {
    window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_IMAGE);
};

export interface GeneratedImageMenuCallbacks {
  onCopyPrompt: () => void;
  onDelete: () => void;
  onEditWithAi?: () => void;
  isPromptCopied?: boolean;
}

export const getGeneratedImageMenuItems = ({onCopyPrompt, onDelete, onEditWithAi, isPromptCopied = false}: GeneratedImageMenuCallbacks): ControlledListItemType[] => {
  const editWithAiMenuItem: ControlledListItemType[] = onEditWithAi
    ? [
        {
          id: 'edit-with-ai',
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
          text: window.i18next.t('pmwjs_edit_with_ai'),
          icon: 'icon-ai-caption',
          isSelected: false,
          className: 'radius-8',
          textClasses: 'content-ai',
          iconClasses: 'content-ai',
          onClick: onEditWithAi,
        },
      ]
    : [];

  return [
    ...editWithAiMenuItem,
    {
      id: 'copy-prompt',
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: isPromptCopied ? window.i18next.t('pmwjs_prompt_copied') : window.i18next.t('pmwjs_copy_prompt'),
      icon: isPromptCopied ? 'icon-check' : 'icon-copy',
      color: isPromptCopied ? ColorTypes.success : ColorTypes.default,
      isSelected: false,
      className: 'radius-8',
      onClick: onCopyPrompt,
    },
    {
      id: 'delete',
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: window.i18next.t('pmwjs_delete'),
      icon: 'icon-delete',
      isSelected: false,
      className: 'radius-8',
      color: ColorTypes.danger,
      onClick: onDelete,
    },
  ];
};

export const openEditWithAiForActiveImage = (): void => {
  if (isMobile) {
    openAiEditModal();
    return;
  }
  window.PMW.redux.store.dispatch(updateFeatureToShowOnLoad(FeatureToShowOnLoad.AI_EDIT));
};

export const confirmAndDeleteGeneratedImage = (item: AITextToImageBackendData, gridId: string, gridSelectionGroupId: string): void => {
  openConfirmDeleteModal({
    title: window.i18next.t('pmwjs_delete_image'),
    text: window.i18next.t('pmwjs_delete_generated_image_confirmation'),
    showBtnDeletingState: true,
    onDeleteConfirmation: async (): Promise<void> => {
      await deleteGeneratedImage(item.id);
      window.PMW.redux.store.dispatch(
        deleteGridItems({
          gridId,
          gridSelectionGroupId,
          itemIdsToDelete: [item.id],
        })
      );
    },
  });
};

export const getDirPathForGeneratedAiImage = (sizeType: MediaItemSizeType): string => {
  const subDirForSize = getDirForMediaSizeType(sizeType);
  return `${BASE_DIR}${subDirForSize ? `/${subDirForSize}` : ''}`;
};

export const getUrlForGeneratedAiImage = (hashedFilename: string, ext: string, sizeType: MediaItemSizeType): string => {
  return repoURL(`${getDirPathForGeneratedAiImage(sizeType)}/${hashedFilename}.${ext}`, getWriteBucket());
};

export const getGridIdForPanel = (panelId: string): string => {
  return `${panelId}-text-to-image-grid`;
};
